import type { SampleOosPrediction } from "@/dev/sample-out-of-stock-predictions";
import {
  type QaItemKey,
  useCoverageBetaStore,
} from "@/store/useCoverageBetaStore";

/**
 * Shared "frame this box" behaviour for the Coverage Beta query image —
 * previously duplicated (with these same magic numbers) across a box click,
 * a sidebar row click, and the area-nav hotkeys. Centralised here so the QA
 * pager's `useFocusActiveQaItem` effect (hooks/useFocusActiveQaItem.ts) and
 * every manual click handler agree on exactly what "focus" means.
 */
const FOCUS_SCALE = 2.5;
const FOCUS_DURATION_MS = 300;

const focusBox = (selector: string): boolean => {
  const box = document.querySelector<HTMLElement>(selector);
  const { pinchRef } = useCoverageBetaStore.getState();
  if (!box || !pinchRef) return false;
  pinchRef.zoomToElement(box, FOCUS_SCALE, FOCUS_DURATION_MS);
  return true;
};

/** Frame an empty area's box (matches QueryEmptyAreaDetectionBox's `data-empty-area-index`). */
export const focusEmptyAreaBox = (feEmptyAreaIdx: number): boolean =>
  focusBox(`[data-empty-area-index="${feEmptyAreaIdx}"]`);

/** Frame a person box (matches QueryPersonBox's `data-person-index`). */
const focusPersonBox = (personIdx: number): boolean =>
  focusBox(`[data-person-index="${personIdx}"]`);

/** Frame the QA pager's current item, whichever kind it is. */
export const focusQaItem = (key: QaItemKey): boolean => {
  if (key.startsWith("area:")) {
    return focusEmptyAreaBox(Number(key.slice("area:".length)));
  }
  return focusPersonBox(Number(key.slice("person:".length)));
};

/**
 * Switch the shelf-images view to whichever reference image first matches
 * this empty area, so clicking/paging to an area also brings up the shelf
 * photo that actually shows the match — not just whatever reference image was
 * last open. Falls back to leaving the current reference image as-is when the
 * area has no matches (e.g. a human-drawn box with no auto-detected OOS
 * predictions).
 */
export const focusRefImgForArea = (
  feEmptyAreaIdx: number,
  predictions: SampleOosPrediction[],
): void => {
  const { activeRefImgIdx, setActiveRefImgIdx } =
    useCoverageBetaStore.getState();

  const emptyAreaIdxToFocusRefImgIdx: Record<number, number> = {};
  for (const oosPrediction of predictions) {
    const firstRefImgMatch = oosPrediction.matches_products_reference_images[0];
    if (!firstRefImgMatch) continue;
    emptyAreaIdxToFocusRefImgIdx[oosPrediction.empty_area_index] ??=
      firstRefImgMatch.reference_image_index;
  }

  setActiveRefImgIdx(
    emptyAreaIdxToFocusRefImgIdx[feEmptyAreaIdx] !== undefined
      ? emptyAreaIdxToFocusRefImgIdx[feEmptyAreaIdx]
      : activeRefImgIdx,
  );
};
